// src/modules/app-views/omnichannel/pages/Init/components/ConversationFooter.jsx
// *******************************************************************************
// CONVERSATION FOOTER
// *******************************************************************************

import { useState } from 'react';
import { Button, Flex, FloatButton, Form, Input, Tooltip } from 'antd';
import {
  AudioOutlined,
  PaperClipOutlined,
  RocketOutlined,
  SendOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import TranslateLanguageSelect from './TranslateLanguageSelect';
import translateX from 'utils/translateX';

// *******************************************************************************
// COMPONENT
// *******************************************************************************

const ConversationFooter = ({
  // Refs
  formRef,
  inputRef,
  // State
  sending,
  outboundLang,
  floatOpen,
  floatActions,
  isInQueue,
  isMetaWindowClosed,
  isChannelRemoved,
  isChannelDisconnected,
  cannotRespond,
  isResolved,
  BLOCK_VIEW_CONVERSATION_NOT_ASSIGN_TO,
  // Handlers
  onSend,
  onOutboundLangChange,
  onEmptyClick,
  onFloatOpenChange,
  onFloatAction,
  // Embedded mode (inside LeftDrawer — hides omnichannel-specific actions)
  embedded,
  // Theme
  isDark,
  bodyBg,
  token,
}) => {
  // Local state — avoids re-rendering the entire Conversation tree on every keystroke
  const [msgText, setMsgText] = useState('');

  // Wrap onSend to clear local text after submit
  const handleSubmit = values => {
    onSend(values);
    setMsgText('');
  };

  return (
    <div
      className="chat-content-footer"
      style={{
        ...bodyBg,
        backgroundRepeat: 'repeat',
        backgroundSize: '400px',
      }}
    >
      <style>{`
        .chat-content-footer .ant-float-btn-group,
        .chat-content-footer .ant-float-btn-group-wrap,
        .chat-content-footer .ant-float-btn { z-index: 5 !important; }
        .chat-content-footer .ant-input-affix-wrapper {
          border-radius: 24px !important;
          border: none !important;
          background-color: ${isDark ? '#202c33' : '#ffffff'} !important;
          box-shadow: ${isDark ? '0 2px 6px rgba(0,0,0,0.45)' : '0 2px 6px rgba(11,20,26,0.18)'} !important;
        }
        .chat-content-footer .ant-input-affix-wrapper:focus-within {
          outline: none !important;
          box-shadow: ${isDark ? '0 2px 6px rgba(0,0,0,0.45)' : '0 2px 6px rgba(11,20,26,0.18)'} !important;
        }
        .chat-content-footer .ant-input {
          background-color: transparent !important;
        }
        .chat-content-footer .ant-input-affix-wrapper-disabled {
          opacity: 0.65 !important;
          background-color: ${isDark ? '#1a2329' : '#f5f5f5'} !important;
        }
      `}</style>
      <Flex
        align="center"
        gap={8}
        style={{ width: '100%', justifyContent: (isInQueue || isChannelRemoved || isChannelDisconnected || cannotRespond) ? 'flex-end' : 'stretch' }}
      >
        {/* Outbound language selector — hidden when channel is removed or user cannot respond */}
        {!isInQueue && !isChannelRemoved && !isChannelDisconnected && !cannotRespond && (
          <Tooltip title={translateX('outbound_translate_lang')}>
            <div
              style={{
                height: 52,
                width: 72,
                borderRadius: 24,
                backgroundColor: isDark ? '#202c33' : '#ffffff',
                boxShadow: isDark
                  ? '0 2px 6px rgba(0,0,0,0.45)'
                  : '0 2px 6px rgba(11,20,26,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <TranslateLanguageSelect
                value={outboundLang}
                onChange={onOutboundLangChange}
                disabled={isInQueue || isMetaWindowClosed}
                variant="borderless"
                noTooltip
                style={{ height: 52, width: 72 }}
              />
            </div>
          </Tooltip>
        )}

        {/* Hide message input when conversation is in queue, channel is removed, or user cannot respond */}
        {!isInQueue && !isChannelRemoved && !isChannelDisconnected && !cannotRespond && (
          <Form name="msgInput" ref={formRef} onFinish={handleSubmit} style={{ flex: 1 }}>
            <Form.Item name="newMsg" className="mb-0">
              <Input
                ref={inputRef}
                autoComplete="off"
                placeholder={isMetaWindowClosed ? translateX('window_closed') : 'Type a message...'}
                size="large"
                disabled={isMetaWindowClosed}
                onChange={e => setMsgText(e.target.value)}
                onPressEnter={() => formRef.current?.submit()}
                prefix={
                  <Flex align="center" gap={12} style={{ marginRight: 4, opacity: isMetaWindowClosed ? 0.35 : 1 }}>
                    <a
                      href="/#"
                      className="font-size-lg"
                      style={{ color: token.colorTextSecondary, pointerEvents: isMetaWindowClosed ? 'none' : undefined }}
                      onClick={onEmptyClick}
                    >
                      <SmileOutlined />
                    </a>
                    <a
                      href="/#"
                      className="font-size-lg"
                      style={{ color: token.colorTextSecondary, pointerEvents: isMetaWindowClosed ? 'none' : undefined }}
                      onClick={onEmptyClick}
                    >
                      <PaperClipOutlined />
                    </a>
                  </Flex>
                }
                suffix={
                  msgText.trim() ? (
                    <Button
                      shape="circle"
                      type="primary"
                      htmlType="submit"
                      loading={sending}
                      disabled={isMetaWindowClosed}
                      style={{ width: 36, height: 36, minWidth: 36 }}
                    >
                      <SendOutlined />
                    </Button>
                  ) : (
                    <a
                      href="/#"
                      className="font-size-lg"
                      style={{ color: token.colorTextSecondary, opacity: isMetaWindowClosed ? 0.35 : 1, pointerEvents: isMetaWindowClosed ? 'none' : undefined }}
                      onClick={onEmptyClick}
                    >
                      <AudioOutlined />
                    </a>
                  )
                }
              />
            </Form.Item>
          </Form>
        )}

        {/* Inline float action button — escalate, transfer, close (hidden in embedded/drawer mode or when user cannot respond) */}
        {/* z-index 5: must stay below the translation overlay (z-index 10) and queue overlay */}
        {!embedded && !(BLOCK_VIEW_CONVERSATION_NOT_ASSIGN_TO && isInQueue) && !isResolved && !cannotRespond && (
          <div
            style={{ position: 'relative', width: 48, height: 48, flexShrink: 0, zIndex: 5 }}
          >
            <FloatButton.Group
              type="primary"
              shape="circle"
              trigger="click"
              open={floatOpen}
              onOpenChange={onFloatOpenChange}
              icon={<RocketOutlined />}
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                zIndex: 5,
              }}
            >
              {floatActions.map(item => (
                <FloatButton
                  key={item.key}
                  type={item.type}
                  icon={item.icon}
                  tooltip={{ title: translateX(item.tooltipText), placement: 'left' }}
                  onClick={() => onFloatAction(item.onClick)}
                />
              ))}
            </FloatButton.Group>
          </div>
        )}
      </Flex>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************

export default ConversationFooter;
